import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Shield, Zap, BarChart3, MessageSquare, Bell, Headphones, Clock, Calendar, ArrowRight, GraduationCap, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const FEATURES = [
  { icon: Zap, text: "إشارات تداول مباشرة لجميع الأصول" },
  { icon: BarChart3, text: "تحليل ذكاء اصطناعي متقدم" },
  { icon: MessageSquare, text: "محادثة AI تفاعلية" },
  { icon: Bell, text: "تنبيهات فورية" },
  { icon: Headphones, text: "دعم فني على مدار الساعة" },
];

export default function Subscribe() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subInfo, setSubInfo] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/', { replace: true });
      return;
    }
    api.get('/subscription/status').then(res => setSubInfo(res.data)).catch(() => {});
  }, [user, navigate]);

  const isActive = subInfo?.status === 'active';
  const startDate = subInfo?.start_date ? new Date(subInfo.start_date) : null;
  const endDate = subInfo?.end_date ? new Date(subInfo.end_date) : null;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full overflow-hidden border-2 border-[#00E5FF]/30">
            <img
              src="https://customer-assets.emergentagent.com/fe0d89dd-7220-47e5-8912-618386cce67e/DALL%C2%B7E%202025-01-28%2017.56.37%20-%20A%20modern,%20professional%20logo%20representing%20a%20trading%20signals%20platform.%20The%20logo%20features%20the%20Arabic%20word%20'%D8%A7%D9%84%D8%B1%D8%A4%D9%8A%D9%87'%20(Al-Ruya)%20in%20a%20sleek,%20stylized%20fon.webp"
              alt="ALROYAH"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-white">الرؤية للتداول</h1>
        </div>

        <div className="bg-gradient-to-b from-[#0d1117] to-[#0a0e14] border border-[#00E5FF]/20 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#00E5FF]/10 to-transparent p-6 border-b border-white/5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white" data-testid="plan-name">الوصول الكامل</h2>
                <p className="text-zinc-400 text-sm mt-1">Full Access</p>
              </div>
              <div className="text-left">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-[#00E5FF]" data-testid="plan-price">$159</span>
                  <span className="text-zinc-500 text-sm">/شهر</span>
                </div>
                <p className="text-zinc-500 text-xs">30 يوم</p>
              </div>
            </div>
          </div>

          {/* Active Subscription Info */}
          {isActive && (
            <div className="p-6 space-y-3" data-testid="subscription-info">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-bold">
                    {'اشتراك فعال'}
                  </span>
                </div>
                <div className="space-y-2">
                  {startDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400 text-sm flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" /> تاريخ البداية
                      </span>
                      <span className="text-white text-sm font-medium" data-testid="sub-start-date">
                        {startDate.toLocaleDateString('ar-SA')}
                      </span>
                    </div>
                  )}
                  {endDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400 text-sm flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" /> تاريخ الانتهاء
                      </span>
                      <span className="text-white text-sm font-medium" data-testid="sub-end-date">
                        {endDate.toLocaleDateString('ar-SA')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> الأيام المتبقية
                    </span>
                    <span className={`text-sm font-bold ${subInfo.remaining_days > 7 ? 'text-emerald-400' : subInfo.remaining_days > 3 ? 'text-amber-400' : 'text-red-400'}`} data-testid="sub-remaining">
                      {subInfo.remaining_days} يوم
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/')}
                className="w-full py-3 bg-[#00E5FF] hover:bg-[#00E5FF]/80 text-black font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                data-testid="go-dashboard-btn"
              >
                <ArrowRight className="w-5 h-5" />
                العودة للوحة التحكم
              </button>
            </div>
          )}

          {/* Expired / No Subscription */}
          {!isActive && (
            <>
              {/* Features */}
              <div className="p-6 space-y-3">
                {FEATURES.map((f, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center shrink-0">
                      <f.icon className="w-4 h-4 text-[#00E5FF]" />
                    </div>
                    <span className="text-zinc-300 text-sm">{f.text}</span>
                  </div>
                ))}
              </div>

              {/* Contact Admin */}
              <div className="p-6 pt-2 space-y-4">
                {subInfo?.status === 'expired' && (
                  <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 p-3 rounded-lg text-sm text-center" data-testid="expired-msg">
                    انتهت صلاحية اشتراكك. تواصل مع الإدارة للتجديد.
                  </div>
                )}

                <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 text-center space-y-2" data-testid="contact-admin-box">
                  <Shield className="w-8 h-8 text-[#00E5FF] mx-auto" />
                  <p className="text-white font-bold">للاشتراك تواصل مع الإدارة</p>
                  <p className="text-zinc-400 text-sm">يتم تفعيل اشتراكك يدوياً لمدة 30 يوم</p>
                </div>

                {/* Free learning bridge — keeps new users engaged while
                    waiting for manual activation instead of hitting a dead-end. */}
                <button
                  type="button"
                  onClick={() => navigate('/learn')}
                  className="w-full bg-gradient-to-r from-amber-500/15 to-amber-400/5 hover:from-amber-500/25 hover:to-amber-400/10 border border-amber-400/40 rounded-xl p-4 flex items-center gap-3 transition group"
                  data-testid="free-learn-bridge-btn"
                >
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-amber-400/20 border border-amber-400/50 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-amber-300" />
                  </div>
                  <div className="flex-1 text-right">
                    <div className="flex items-center justify-end gap-1.5 text-amber-300 font-bold text-sm">
                      <span>ابدأ التعلّم مجّاناً الآن</span>
                      <Sparkles className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-zinc-400 text-xs mt-0.5">
                      70+ درساً تفاعليّاً — لا يتطلّب اشتراكاً
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-amber-300 group-hover:-translate-x-1 transition-transform rotate-180" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Logout */}
        <div className="text-center mt-6">
          <button
            onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }}
            className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
            data-testid="subscribe-logout-btn"
          >
            تسجيل الخروج
          </button>
        </div>
      </motion.div>
    </div>
  );
}
